import { c as _c } from "react/compiler-runtime";
import { BinIcon, PlusIcon } from '@systemeio/ui/icons';
import { Hint, NeutralButton, NeutralTypeEnum, DateTimePicker } from '@systemeio/ui';
import { greenwichTimeZone } from 'modules/calendar/constants';
import ButtonLikeLink from 'shared/components/button-like-link';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { twMerge } from 'tailwind-merge';
import { useDraftUnavailabilitySlots } from '../hooks/use-draft-availability-slots';
import { AvailabilitySlotsSkeleton } from './availability-slots-skeleton';
import { AvailabilityErrors } from 'modules/calendar/availability/types/availability-interface';
interface DateUnavailabilitySlotsProps extends Omit<ReturnType<typeof useDraftUnavailabilitySlots>, 'reinitiate'> {
  isLoading?: boolean;
  disabled?: boolean;
  titleClassName?: string;
  errors?: AvailabilityErrors;
}
export function DateUnavailabilitySlots(t0) {
  const $ = _c(45);
  const {
    slots,
    addSlot,
    updateSlot,
    removeSlot,
    isLoading,
    disabled,
    titleClassName,
    errors
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    intlLocale
  } = useUser();
  const t1 = slots.length > 0 && "mb-5";
  let t2;
  if ($[0] !== t1 || $[1] !== titleClassName) {
    t2 = twMerge("flex w-full flex-col items-start gap-2 md:flex-row md:items-center md:justify-between", t1, titleClassName);
    $[0] = t1;
    $[1] = titleClassName;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("dashboard.calendar.time_off_tooltip");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("dashboard.calendar.time_off");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <p>{t4}</p>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <Hint label={t3}>{t5}</Hint>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== addSlot) {
    t7 = () => {
      addSlot({
        startDate: "",
        endDate: ""
      });
    };
    $[12] = addSlot;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <PlusIcon />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t) {
    t9 = t("dashboard.calendar.add_date_slot");
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== disabled || $[18] !== t7 || $[19] !== t9) {
    t10 = <ButtonLikeLink type="button" onClick={t7} className="flex items-center gap-1 text-sm" disabled={disabled}>{t8}{t9}</ButtonLikeLink>;
    $[17] = disabled;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t2 || $[23] !== t6) {
    t11 = <div className={t2}>{t6}{t10}</div>;
    $[21] = t10;
    $[22] = t2;
    $[23] = t6;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== isLoading) {
    t12 = isLoading && <AvailabilitySlotsSkeleton />;
    $[25] = isLoading;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== disabled || $[28] !== errors || $[29] !== intlLocale || $[30] !== isLoading || $[31] !== removeSlot || $[32] !== slots || $[33] !== t || $[34] !== updateSlot || $[35] !== user?.createdAt || $[36] !== user?.dateFormat || $[37] !== user?.timeFormat) {
    t13 = !isLoading && slots?.map((slot, index) => {
      const isTimeBeforeEnd = time => {
        const selectedTime = new Date(time).getTime();
        const currentTime = new Date().getTime();
        const endTime = slot.endDate ? new Date(slot.endDate).getTime() : Infinity;
        return selectedTime > currentTime && selectedTime < endTime;
      };
      const isTimeAfterStart = time_0 => {
        const selectedTime_0 = new Date(time_0).getTime();
        const startTime = new Date(slot.startDate || new Date()).getTime();
        return selectedTime_0 > startTime;
      };
      return <div key={index} className="border-text-secondary/30 flex flex-row flex-wrap items-end justify-between gap-2 gap-y-3 border-t py-5 last:border-b"><div className="flex flex-wrap items-center gap-2"><DateTimePicker label={t("global.date_from")} placeholder={t("global.select_date_and_time")} value={slot.startDate} onChange={startDate => {
            updateSlot({
              ...slot,
              ...(startDate ? {
                startDate
              } : {})
            }, index);
          }} customProps={{
            minDate: new Date(),
            maxDate: new Date(slot.endDate),
            filterTime: isTimeBeforeEnd
          }} disabled={disabled} timezone={greenwichTimeZone} intlLocale={intlLocale} createdAt={user?.createdAt} userDateFormat={user?.dateFormat} userTimeFormat={user?.timeFormat} disableApplyButton={date => !isTimeBeforeEnd(date)} error={errors?.[`unavailabilityRanges[${index}].startDate`]} /><span className="bg-background-tertiary mt-6 hidden h-[2px] w-3 rounded-full sm:block" /><DateTimePicker label={t("global.date_to")} placeholder={t("global.select_date_and_time")} value={slot.endDate} onChange={endDate => {
            updateSlot({
              ...slot,
              ...(endDate ? {
                endDate
              } : {})
            }, index);
          }} customProps={{
            minDate: new Date(slot.startDate || new Date()),
            filterTime: isTimeAfterStart
          }} disabled={disabled} timezone={greenwichTimeZone} intlLocale={intlLocale} createdAt={user?.createdAt} userDateFormat={user?.dateFormat} userTimeFormat={user?.timeFormat} disableApplyButton={date_0 => !isTimeAfterStart(date_0)} error={errors?.[`unavailabilityRanges[${index}].endDate`]} /></div><NeutralButton neutralType={NeutralTypeEnum.danger} type="button" onClick={() => removeSlot(index)} disabled={disabled}><BinIcon width={16} height={16} className="fill-text-primary group-hover:fill-danger transition-all" /><span className="hidden sm:block">{t("global.remove")}</span></NeutralButton></div>;
    });
    $[27] = disabled;
    $[28] = errors;
    $[29] = intlLocale;
    $[30] = isLoading;
    $[31] = removeSlot;
    $[32] = slots;
    $[33] = t;
    $[34] = updateSlot;
    $[35] = user?.createdAt;
    $[36] = user?.dateFormat;
    $[37] = user?.timeFormat;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t12 || $[40] !== t13) {
    t14 = <div>{t12}{t13}</div>;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t11 || $[43] !== t14) {
    t15 = <>{t11}{t14}</>;
    $[42] = t11;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  return t15;
}